import React, { useContext, useEffect } from "react";
import {
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { Box } from "@mui/material";
import ComponentLoading from "./components/App/ComponentLoading";
import {
  buildIrasOAuthReturnTarget,
  readIrasOAuthReturnLocation,
} from "./components/CompanyPage/CorrespondencesTab/irasOAuthHelpers";

import { AuthContext } from "./contexts/AuthContext";

import Header from "./components/App/Header";

//pages
import AuthPage from "./pages/AuthPage";
import AuthVerificationPage from "./pages/Auth/AuthVerificationPage";
import MicrosoftSsoCallbackPage from "./pages/Auth/MicrosoftSsoCallbackPage";
import LogoutPage from "./pages/LogoutPage";
import ConsolePanel from "./pages/ConsolePanel";
import CompanyPage from "./pages/CompanyPage";
import SettingsPage from "./pages/SettingsPage";
import ForgotPasswordPage from "./pages/Auth/ForgotPasswordPage";
import ResetPasswordPage from "./pages/Auth/ResetPasswordPage";
import TaxComputationConsole from "./pages/TaxComputationConsole";

// internal (admin)
import AdminRoutes from "./admin/routes/AdminRoutes";
import AdminGuard from "./admin/guards/AdminGuard";
import AdminLayout from "./admin/layouts/AdminLayout";

const WithHeaderLayout = React.memo(({ isAuthenticated }) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        overflow: "hidden",
      }}
    >
      <Header isAuthenticated={isAuthenticated} />
      <Box
        component='main'
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          width: "100%",
          height: "100vh",
          overflow: "hidden",
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
});

// Partner claim links are sometimes copied as
// /auth?request_type=register?promo_code=CODE. Peel that into real query params
// before AuthPage treats the whole string as an unknown request type.
const normalizeRegisterPromoSearch = (search) => {
  const params = new URLSearchParams(
    search.startsWith("?") ? search.slice(1) : search
  );
  const raw = params.get("request_type") || "";
  const marker = "register?";
  if (!raw.startsWith(marker)) return null;
  params.set("request_type", "register");
  const embedded = new URLSearchParams(raw.slice(marker.length));
  embedded.forEach((value, key) => {
    if (!params.has(key)) params.set(key, value);
  });
  return `?${params.toString()}`;
};

const AuthEntry = () => {
  const location = useLocation();
  const search = normalizeRegisterPromoSearch(location.search);
  if (search) {
    return <Navigate to={{ pathname: "/auth", search }} replace />;
  }
  return <AuthPage />;
};

const IrasCallbackRedirectPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const target = buildIrasOAuthReturnTarget(
      readIrasOAuthReturnLocation(),
      location.search
    );
    // Stay in the same SPA session. A hard reload here races Safari cookie
    // restore after Apex/Singpass and can look like a Tax Savvy session expiry.
    navigate(`${target.pathname}${target.search}${target.hash}`, {
      replace: true,
    });
  }, [location.hash, location.search, navigate]);

  return (
    <ComponentLoading
      message='Returning from CorpPass…'
      fullHeight
    />
  );
};

const AppRoutes = () => {
  const { global_authState } = useContext(AuthContext);
  const isAuthenticated = global_authState.uid !== null;

  return (
    <Routes>
      <Route
        path='/internal/*'
        element={
          <AdminGuard>
            <AdminLayout>
              <AdminRoutes />
            </AdminLayout>
          </AdminGuard>
        }
      />

      <Route element={<WithHeaderLayout isAuthenticated={isAuthenticated} />}>
        <Route path='/' element={<Navigate to='/auth' replace />} />
        <Route path='/auth' element={<AuthEntry />} />
        <Route path='/auth/verification' element={<AuthVerificationPage />} />
        <Route
          path='/auth/sso/microsoft/callback'
          element={<MicrosoftSsoCallbackPage />}
        />
        <Route path='/iras/callback' element={<IrasCallbackRedirectPage />} />
        <Route path='/logout' element={<LogoutPage />} />
        <Route path='/password-reset' element={<ForgotPasswordPage />} />
        <Route path='/password-reset/confirm' element={<ResetPasswordPage />} />
        <Route path='/settings' element={<SettingsPage />} />
        <Route path='/consolePanel' element={<ConsolePanel />} />
        <Route path='/company' element={<CompanyPage />} />
        <Route path='/main-doc' element={<TaxComputationConsole />} />
        <Route path='*' element={<Navigate to='/auth' replace />} />
      </Route>
    </Routes>
  );
};

export default AppRoutes;
